import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Quote, 
  LogOut, 
  ChevronDown, 
  LayoutDashboard, 
  ChevronRight, 
  FileCheck, 
  Layers, 
  PanelLeft, 
  Calendar,
  Home,
  Landmark,
  GraduationCap,
  FileEdit,
  Activity,
  Building,
  Newspaper,
  Users,
  Briefcase,
  ShieldCheck,
  WifiOff,
  AlertTriangle,
  AlertCircle,
  PanelBottom,
  ClipboardList,
  CalendarClock,
  CalendarRange,
  Wallet,
  TicketPercent,
  Globe,
  UserCheck,
  CreditCard,
  HelpCircle
} from 'lucide-react';
import './DashboardLayout.css';

/**
 * Executive Portal Layout (Satu UNPAM Architecture)
 * - Full-height left sidebar with branding, navigation, and user profile at the bottom
 * - Outside collapse toggle on the content header
 * - Unified hierarchy for menus & submenus with active indicators
 * - Smooth native scrolling for all forms and contents
 */

const MENU_ICONS = {
  'homepage': Home,
  'about-us': Landmark,
  'education-program': GraduationCap,
  'admissions': FileEdit,
  'student-life': Activity,
  'facilities': Building,
  'news-updates': Newspaper,
  'parents': Users,
  'careers': Briefcase,
  'footer': PanelBottom,
};

function DashboardLayout({ 
  user, 
  onLogout, 
  children, 
  onTabChange, 
  userRole = 'admin',
  activeTab: controlledActiveTab,
  pendingCount = 0,
  pendingTestimonialsCount = 0,
  onlineUsersCount = 0,
  applicantsCount = 0,
  levelCounts = {},
  totalApplicants = 0,
  totalChildrenCount = 0,
  scheduledTestsCount = 0,
  pendingPaymentCount = 0,
  pendingFeesCount = 0,
  admissionsSubmenuPending = { periods: 0, levels: 0, test_slots: 0, promo_codes: 0, total: 0 },
  isProfileComplete = true,
  apiBase = 'https://permataharapansecondary.sch.id/api'
}) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth > 768);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expandCMS, setExpandCMS] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [expandApplicants, setExpandApplicants] = useState(true);
  const [expandAdmContent, setExpandAdmContent] = useState(false);
  const [expandPrincipalAdmissions, setExpandPrincipalAdmissions] = useState(true);
  const internalActiveTabState = useState('dashboard');
  const [internalActiveTab, setInternalActiveTab] = internalActiveTabState;
  const dropdownRef = useRef(null);
  const contentScrollRef = useRef(null);

  // Online / Offline and Session Status
  const [isOnline, setIsOnline] = useState(() => typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);

  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

  // Auto-scroll content body to top when tab changes
  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  // Formatted date string (e.g., Thursday, 15 October 2026)
  const currentFormattedDate = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  // Handle mobile window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle network online / offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check session validity periodically and on focus
  const checkSessionHealth = useCallback(async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }

    try {
      // 1. Check local session timestamp
      const storedSession = localStorage.getItem('userSession');
      if (storedSession) {
        try {
          const parsed = JSON.parse(storedSession);
          const maxAge = 24 * 60 * 60 * 1000;
          if (parsed.timestamp && Date.now() - parsed.timestamp > maxAge) {
            setSessionExpired(true);
            setShowSessionModal(true);
            return;
          }
        } catch (_) {}
      }

      // 2. Heartbeat to /auth/me or /admissions/me for parent
      const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const endpoint = userRole === 'parent' ? `${apiBase}/admissions/me` : `${apiBase}/auth/me`;
      const res = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        headers
      });

      if (res.status === 401 || res.status === 403) {
        setSessionExpired(true);
        setShowSessionModal(true);
      } else if (res.ok) {
        setSessionExpired(false);
      }
    } catch (_) {
      // Transient network errors are caught by online/offline listener
    }
  }, [apiBase]);

  useEffect(() => {
    checkSessionHealth();
    const interval = setInterval(checkSessionHealth, 60000);

    const handleFocus = () => {
      checkSessionHealth();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkSessionHealth]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleLogout = useCallback(async () => {
    setDropdownOpen(false);
    setShowSessionModal(false);
    if (onLogout) {
      await onLogout();
    }
  }, [onLogout]);

  const getUserInitials = useCallback(() => {
    if (!user?.full_name) {
      if (userRole === 'parent') return 'OT';
      return userRole === 'approver' || userRole === 'principal' ? 'P' : 'WA';
    }
    return user.full_name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [user?.full_name, userRole]);

  const handleTabChange = useCallback((tabId) => {
    setInternalActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [onTabChange, isMobile]);

  // Comprehensive CMS menus definition
  const MENUS = [
    {
      id: 'homepage',
      title: 'Homepage',
      slug: 'homepage',
      sections: [
        { id: 'cms-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-quicklinks', title: 'Quick Links', slug: 'quicklinks' },
        { id: 'cms-highlights', title: 'Highlights', slug: 'highlights' },
        { id: 'cms-news', title: 'News', slug: 'news' },
        { id: 'cms-countdown', title: 'Countdown', slug: 'countdown' }
      ]
    },
    {
      id: 'about-us',
      title: 'About Us',
      slug: 'about-us',
      sections: [
        { id: 'cms-about-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-about-principal', title: 'Principal Message', slug: 'principal' },
        { id: 'cms-about-values', title: 'Vision, Mission & Values', slug: 'values' },
        { id: 'cms-about-philosophy', title: 'Philosophy', slug: 'philosophy' },
        { id: 'cms-about-history', title: 'History', slug: 'history' },
        { id: 'cms-about-leadership', title: 'Leadership', slug: 'leadership' }
      ]
    },
    {
      id: 'education-program',
      title: 'Education Program',
      slug: 'education-program',
      sections: [
        { id: 'cms-education-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-edu-programs', title: 'Program Levels', slug: 'programs' },
        { id: 'cms-edu-curriculum', title: 'Curriculum', slug: 'curriculum' },
        { id: 'cms-edu-subjects', title: 'Subjects', slug: 'subjects' },
        { id: 'cms-edu-learning', title: 'Learning Approach', slug: 'learning' },
        { id: 'cms-edu-assessment', title: 'Assessment', slug: 'assessment' },
        { id: 'cms-edu-special-programs', title: 'Special Programs', slug: 'special-programs' },
        { id: 'cms-edu-secondary', title: 'Secondary Detail Page', slug: 'secondary' }
      ]
    },
    {
      id: 'admissions',
      title: 'Admissions',
      slug: 'admissions',
      sections: [
        { id: 'cms-adm-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-adm-levels', title: 'Admission Levels', slug: 'levels' },
        { id: 'cms-adm-process', title: 'Application Process', slug: 'process' },
        { id: 'cms-adm-requirements', title: 'Requirements', slug: 'requirements' },
        { id: 'cms-adm-timeline', title: 'Key Dates', slug: 'timeline' },
        { id: 'cms-adm-fees', title: 'Fees & Payment', slug: 'fees' },
        { id: 'cms-adm-online-app', title: 'Online Application', slug: 'online-app' }
      ]
    },
    {
      id: 'student-life',
      title: 'Student Life',
      slug: 'student-life',
      sections: [
        { id: 'cms-sl-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-sl-clubs', title: 'Clubs & ECA', slug: 'clubs' },
        { id: 'cms-sl-events', title: 'Event & Competition', slug: 'events' },
        { id: 'cms-sl-houses', title: 'House System', slug: 'houses' },
        { id: 'cms-sl-house-details', title: 'House Detail Pages', slug: 'house-details' },
        { id: 'cms-sl-wellbeing', title: 'Wellbeing & SEL', slug: 'wellbeing' },
        { id: 'cms-sl-character', title: 'Character Shaping', slug: 'character' },
        { id: 'cms-sl-leaps', title: 'LEAPS', slug: 'leaps' }
      ]
    },
    {
      id: 'facilities',
      title: 'Facilities',
      slug: 'facilities',
      sections: [
        { id: 'cms-fac-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-fac-overview', title: 'Campus Overview', slug: 'overview' },
        { id: 'cms-fac-overview-details', title: 'Overview Detail Pages', slug: 'overview-details' },
        { id: 'cms-fac-tech', title: 'Technology & Innovation', slug: 'tech' },
        { id: 'cms-fac-tech-details', title: 'Tech Detail Pages', slug: 'tech-details' },
        { id: 'cms-fac-safety', title: 'Safety & Security', slug: 'safety' },
        { id: 'cms-fac-gallery', title: 'Photo Gallery', slug: 'gallery' },
        { id: 'cms-fac-tour', title: 'Virtual Tour', slug: 'tour' }
      ]
    },
    {
      id: 'news-updates',
      title: 'News & Updates',
      slug: 'news-updates',
      sections: [
        { id: 'cms-news-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-news-press', title: 'Press Releases', slug: 'press' },
        { id: 'cms-news-articles', title: 'Articles', slug: 'articles' }
      ]
    },
    {
      id: 'parents',
      title: 'Parents',
      slug: 'parents',
      sections: [
        { id: 'cms-par-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-par-palc', title: 'Learning Community', slug: 'palc' },
        { id: 'cms-par-handbook', title: 'Parent Handbook', slug: 'handbook' },
        { id: 'cms-par-portal', title: 'Portal Access', slug: 'portal' },
        { id: 'cms-par-communication', title: 'Communication Channels', slug: 'communication' }
      ]
    },
    {
      id: 'careers',
      title: 'Careers',
      slug: 'careers',
      sections: [
        { id: 'cms-car-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-car-philosophy', title: 'Hiring Philosophy', slug: 'philosophy' },
        { id: 'cms-car-positions', title: 'Open Positions', slug: 'positions' },
        { id: 'cms-car-opportunities', title: 'Internships & Volunteering', slug: 'opportunities' }
      ]
    },
    {
      id: 'contact-us',
      title: 'Contact Us',
      slug: 'contact-us',
      sections: [
        { id: 'cms-con-hero', title: 'Hero Section', slug: 'hero' },
        { id: 'cms-con-info', title: 'Contact Info & Location', slug: 'info' },
        { id: 'cms-con-faq', title: 'FAQ', slug: 'faq' },
        { id: 'cms-con-partnerships', title: 'Partnerships', slug: 'partnerships' },
        { id: 'cms-con-testimonials', title: 'Testimonials', slug: 'testimonials' },
        { id: 'cms-con-countdown', title: 'Campus Construction', slug: 'countdown' }
      ]
    },
    {
      id: 'footer',
      title: 'Footer',
      slug: 'footer',
      sections: [
        { id: 'cms-footer', title: 'Footer Settings', slug: 'footer' }
      ]
    }
  ];

  // Auto-expand menu ONLY when activeTab is an actual CMS section
  useEffect(() => {
    if (activeTab && activeTab.startsWith('cms-')) {
      MENUS.forEach(menu => {
        if (menu.sections?.some(s => s.id === activeTab)) {
          setExpandCMS(true);
          setExpandedMenus(prev => ({
            ...prev,
            [menu.id]: true
          }));
        }
      });
    }
  }, [activeTab]);

  const toggleMenuExpanded = useCallback((menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  }, []);

  const isSuperAdmin = userRole === 'super_admin';
  const isAdmissions = userRole === 'admissions';
  const isParent = userRole === 'parent';
  const showContentManagement = userRole === 'website_admin' || userRole === 'admin';
  const showApprovalQueue = userRole === 'principal' || userRole === 'approver';

  // Admissions page content is managed by Admin Admissions, not Website Admin
  const WEBSITE_ADMIN_MENUS = MENUS.filter(m => m.id !== 'admissions');
  const ADMISSIONS_CONTENT_SECTIONS = (MENUS.find(m => m.id === 'admissions') || {}).sections || [];

  const ADMISSIONS_LEVEL_ITEMS = [
    { id: 'applicants-preschool', code: 'preschool', title: 'Preschool' },
    { id: 'applicants-kindergarten', code: 'kindergarten', title: 'Kindergarten' },
    { id: 'applicants-primary', code: 'primary', title: 'Primary' },
    { id: 'applicants-secondary', code: 'secondary', title: 'Secondary' },
    { id: 'applicants-sma', code: 'sma', title: 'High School (SMA)' }
  ];

  useEffect(() => {
    if (!isAdmissions || !activeTab) return;
    if (activeTab.startsWith('applicants')) setExpandApplicants(true);
    if (activeTab.startsWith('cms-adm-')) setExpandAdmContent(true);
    if (activeTab.startsWith('admissions-')) setExpandPrincipalAdmissions(true);
  }, [activeTab, isAdmissions]);

  const roleDisplayName = isParent
    ? 'Orang Tua / Wali'
    : userRole === 'approver' || userRole === 'principal' 
    ? 'Principal' 
    : isSuperAdmin 
      ? 'Super Admin' 
      : isAdmissions
        ? 'Admin Admissions'
        : 'Website Admin';

  return (
    <div className={`portal-layout-wrapper ${!sidebarOpen ? 'sidebar-collapsed' : ''}`}>
      {/* MOBILE BACKDROP OVERLAY */}
      {isMobile && sidebarOpen && (
        <div 
          className="portal-sidebar-backdrop" 
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true" 
        />
      )}

      {/* FULL-HEIGHT LEFT SIDEBAR (0 to 100vh) */}
      <aside className={`portal-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        {/* SIDEBAR BRANDING HEADER */}
        <div className="sidebar-brand-header">
          <div className="brand-logo-wrap">
            <img src="/logo_PHSchool.png" alt="Permata Harapan Logo" className="brand-school-logo" />
          </div>
          {sidebarOpen && (
            <div className="brand-title-wrap">
              <span className="brand-main-title">Permata Harapan</span>
              <span className="brand-sub-title">Secondary School</span>
            </div>
          )}
        </div>

        {/* SIDEBAR NAVIGATION ITEMS */}
        <div className="sidebar-nav-scroll">
          <div className="nav-group-header">
            {sidebarOpen ? 'MAIN MENU' : '•••'}
          </div>

          <nav className="nav-items-list">
            {/* Parent Portal Dedicated Navigation */}
            {isParent ? (
              <>
                <button
                  className={`nav-item-btn ${activeTab === 'dashboard' || activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => handleTabChange('dashboard')}
                  title="Ringkasan PPDB"
                >
                  <LayoutDashboard size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Ringkasan PPDB</span>}
                </button>

                <button
                  className={`nav-item-btn ${activeTab === 'children' ? 'active' : ''}`}
                  onClick={() => handleTabChange('children')}
                  title="Pendaftaran Anak"
                >
                  <Users size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Pendaftaran Anak</span>}
                  {totalChildrenCount > 0 && (
                    <span className="nav-badge-count" style={{ background: '#2563eb' }}>
                      {totalChildrenCount}
                    </span>
                  )}
                </button>

                <button
                  className={`nav-item-btn ${activeTab === 'family-profile' ? 'active' : ''}`}
                  onClick={() => handleTabChange('family-profile')}
                  title="Profil Keluarga & Berkas"
                >
                  <UserCheck size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Profil Keluarga</span>}
                  {!isProfileComplete && (
                    <span className="nav-badge-count" style={{ background: '#d97706' }} title="Lengkapi data keluarga">
                      !
                    </span>
                  )}
                </button>

                <button
                  className={`nav-item-btn ${activeTab === 'test-schedule' ? 'active' : ''}`}
                  onClick={() => handleTabChange('test-schedule')}
                  title="Jadwal Tes & LoA"
                >
                  <CalendarClock size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Jadwal Tes & LoA</span>}
                  {scheduledTestsCount > 0 && (
                    <span className="nav-badge-count" style={{ background: '#7c3aed' }}>
                      {scheduledTestsCount}
                    </span>
                  )}
                </button>

                <button
                  className={`nav-item-btn ${activeTab === 'payments' ? 'active' : ''}`}
                  onClick={() => handleTabChange('payments')}
                  title="Pembayaran & NIS"
                >
                  <CreditCard size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Pembayaran & NIS</span>}
                  {pendingPaymentCount > 0 && (
                    <span className="nav-badge-count" style={{ background: '#ea580c' }}>
                      {pendingPaymentCount}
                    </span>
                  )}
                </button>

                <button
                  className={`nav-item-btn ${activeTab === 'guide' ? 'active' : ''}`}
                  onClick={() => handleTabChange('guide')}
                  title="Panduan & Bantuan PPDB"
                >
                  <HelpCircle size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Panduan & Bantuan</span>}
                </button>
              </>
            ) : (
              /* Non-parent default dashboard button */
              <button
                className={`nav-item-btn ${activeTab === 'dashboard' || activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => handleTabChange('dashboard')}
                title="Dashboard"
              >
                <LayoutDashboard size={18} className="nav-icon" />
                {sidebarOpen && <span className="nav-label">{isAdmissions ? 'Ringkasan PPDB' : 'Dashboard'}</span>}
              </button>
            )}

            {/* Admin Admissions Dedicated Navigation */}
            {isAdmissions && (
              <>
                {/* Pendaftar with level sub-menu */}
                <div className="nav-section-wrapper">
                  <button
                    className={`nav-item-btn collapsible ${expandApplicants ? 'expanded' : ''} ${activeTab.startsWith('applicants') && !sidebarOpen ? 'active' : ''}`}
                    onClick={() => {
                      if (!sidebarOpen) {
                        handleTabChange('applicants');
                        return;
                      }
                      setExpandApplicants(!expandApplicants);
                    }}
                    title="Pendaftar"
                  >
                    <ClipboardList size={18} className="nav-icon" />
                    {sidebarOpen && (
                      <>
                        <span className="nav-label">Pendaftar</span>
                        {applicantsCount > 0 && (
                          <span className="nav-badge-count" title="Perlu tindakan">{applicantsCount}</span>
                        )}
                        <ChevronRight size={14} className={`chevron-arrow ${expandApplicants ? 'rotated' : ''}`} />
                      </>
                    )}
                  </button>

                  {expandApplicants && sidebarOpen && (
                    <div className="cms-subsections-tree">
                      <div className="tree-line-guide" />
                      <div className="subsections-items-wrap">
                        {[{ id: 'applicants', code: '', title: 'Semua Pendaftar' }, ...ADMISSIONS_LEVEL_ITEMS].map(item => {
                          const isActive = activeTab === item.id;
                          const count = item.code ? (levelCounts[item.code] || 0) : totalApplicants;
                          return (
                            <button
                              key={item.id}
                              className={`subsection-link-btn ${isActive ? 'active' : ''}`}
                              onClick={() => handleTabChange(item.id)}
                              title={item.title}
                            >
                              <span className={`subsection-bullet ${isActive ? 'active' : ''}`} />
                              <span className="subsection-text">{item.title}</span>
                              <span className="subsection-count">{count}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="nav-group-header">
                  {sidebarOpen ? 'PENGATURAN PPDB' : '•••'}
                </div>

                {[
                  { id: 'periods', title: 'Periode & Kuota', icon: CalendarRange },
                  { id: 'test-slots', title: 'Jadwal Tes', icon: CalendarClock },
                  { id: 'levels-fees', title: 'Jenjang & Biaya', icon: Wallet },
                  { id: 'promo-codes', title: 'Kode Promo & Diskon', icon: TicketPercent }
                ].map(item => {
                  const ItemIcon = item.icon;
                  return (
                    <button
                      key={item.id}
                      className={`nav-item-btn ${activeTab === item.id ? 'active' : ''}`}
                      onClick={() => handleTabChange(item.id)}
                      title={item.title}
                    >
                      <ItemIcon size={18} className="nav-icon" />
                      {sidebarOpen && <span className="nav-label">{item.title}</span>}
                    </button>
                  );
                })}

                <div className="nav-group-header">
                  {sidebarOpen ? 'KONTEN WEBSITE' : '•••'}
                </div>

                <div className="nav-section-wrapper">
                  <button
                    className={`nav-item-btn collapsible ${expandAdmContent ? 'expanded' : ''}`}
                    onClick={() => {
                      if (!sidebarOpen) {
                        setSidebarOpen(true);
                        setExpandAdmContent(true);
                        return;
                      }
                      setExpandAdmContent(!expandAdmContent);
                    }}
                    title="Halaman Admissions"
                  >
                    <Globe size={18} className="nav-icon" />
                    {sidebarOpen && (
                      <>
                        <span className="nav-label">Halaman Admissions</span>
                        <ChevronRight size={14} className={`chevron-arrow ${expandAdmContent ? 'rotated' : ''}`} />
                      </>
                    )}
                  </button>

                  {expandAdmContent && sidebarOpen && (
                    <div className="cms-subsections-tree">
                      <div className="tree-line-guide" />
                      <div className="subsections-items-wrap">
                        {ADMISSIONS_CONTENT_SECTIONS.map(section => {
                          const isActive = activeTab === section.id;
                          return (
                            <button
                              key={section.id}
                              className={`subsection-link-btn ${isActive ? 'active' : ''}`}
                              onClick={() => handleTabChange(section.id)}
                              title={section.title}
                            >
                              <span className={`subsection-bullet ${isActive ? 'active' : ''}`} />
                              <span className="subsection-text">{section.title}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Super Admin Dedicated Navigation Items */}
            {isSuperAdmin && (
              <>
                <button
                  className={`nav-item-btn ${activeTab === 'users' ? 'active' : ''}`}
                  onClick={() => handleTabChange('users')}
                  title="Kelola User & Hak Akses"
                >
                  <Users size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Kelola User</span>}
                </button>

                <button
                  className={`nav-item-btn ${activeTab === 'active-users' ? 'active' : ''}`}
                  onClick={() => handleTabChange('active-users')}
                  title="User Aktif & Monitor IP Address"
                >
                  <Activity size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">User Aktif & IP</span>}
                  {onlineUsersCount > 0 && (
                    <span
                      className="nav-badge-count"
                      style={{ background: '#10b981', color: '#ffffff' }}
                    >
                      {onlineUsersCount}
                    </span>
                  )}
                </button>

                <div className="nav-group-header">
                  {sidebarOpen ? 'ADMISSIONS & PPDB' : '•••'}
                </div>

                <button
                  className={`nav-item-btn ${activeTab === 'admissions-overview' ? 'active' : ''}`}
                  onClick={() => handleTabChange('admissions-overview')}
                  title="Ringkasan Eksekutif PPDB & Data Aktif"
                >
                  <CalendarRange size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Ringkasan PPDB</span>}
                </button>

                <button
                  className={`nav-item-btn ${activeTab === 'applicants' ? 'active' : ''}`}
                  onClick={() => handleTabChange('applicants')}
                  title="Data Pendaftar Siswa"
                >
                  <GraduationCap size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Data Pendaftar</span>}
                  {applicantsCount > 0 && (
                    <span
                      className="nav-badge-count"
                      style={{ background: '#2563eb', color: '#ffffff' }}
                    >
                      {applicantsCount}
                    </span>
                  )}
                </button>

                <button
                  className={`nav-item-btn ${activeTab === 'portal-users' ? 'active' : ''}`}
                  onClick={() => handleTabChange('portal-users')}
                  title="Akun Orang Tua Terdaftar"
                >
                  <Users size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Akun Orang Tua</span>}
                </button>

                <button
                  className={`nav-item-btn ${activeTab === 'signatories' ? 'active' : ''}`}
                  onClick={() => handleTabChange('signatories')}
                  title="Master Pejabat & Penandatangan Dokumen (Principal & Finance)"
                >
                  <UserCheck size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Pejabat & TTD Dokumen</span>}
                </button>

                <div className="nav-group-header">
                  {sidebarOpen ? 'SISTEM & AUDIT' : '•••'}
                </div>

                <button
                  className={`nav-item-btn ${activeTab === 'submissions-monitor' ? 'active' : ''}`}
                  onClick={() => handleTabChange('submissions-monitor')}
                  title="Monitor Antrean & Riwayat Submissions CMS"
                >
                  <FileCheck size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Submissions</span>}
                  {pendingCount > 0 && (
                    <span className="nav-badge-count">{pendingCount}</span>
                  )}
                </button>

                <button
                  className={`nav-item-btn ${activeTab === 'audit-logs' ? 'active' : ''}`}
                  onClick={() => handleTabChange('audit-logs')}
                  title="Audit Keamanan & Riwayat Aktivitas Sistem"
                >
                  <ShieldCheck size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Audit & Log Sistem</span>}
                </button>
              </>
            )}

            {/* 2. Submissions Queue & Testimonials Submissions (Principal / Approver) */}
            {showApprovalQueue && (
              <>
                <button
                  className={`nav-item-btn ${activeTab === 'approvals' ? 'active' : ''}`}
                  onClick={() => handleTabChange('approvals')}
                  title="Content Submissions Queue (All Menus)"
                >
                  <FileCheck size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Submissions</span>}
                  {pendingCount > 0 && (
                    <span className="nav-badge-count">{pendingCount}</span>
                  )}
                </button>

                <button
                  className={`nav-item-btn ${activeTab === 'testimonial-approvals' ? 'active' : ''}`}
                  onClick={() => handleTabChange('testimonial-approvals')}
                  title="Testimonials Submissions Queue"
                >
                  <Quote size={18} className="nav-icon" />
                  {sidebarOpen && <span className="nav-label">Testimonials</span>}
                  {pendingTestimonialsCount > 0 && (
                    <span className="nav-badge-count">{pendingTestimonialsCount}</span>
                  )}
                </button>

                {/* Admissions PPDB Governance for Principal with Submenu */}
                <div className="nav-section-wrapper">
                  <button
                    className={`nav-item-btn collapsible ${expandPrincipalAdmissions ? 'expanded' : ''} ${activeTab.startsWith('admissions-') && !sidebarOpen ? 'active' : ''}`}
                    onClick={() => {
                      if (!sidebarOpen) {
                        handleTabChange('admissions-periods');
                        return;
                      }
                      setExpandPrincipalAdmissions(!expandPrincipalAdmissions);
                    }}
                    title="Admissions & PPDB Governance"
                  >
                    <GraduationCap size={18} className="nav-icon" />
                    {sidebarOpen && (
                      <>
                        <span className="nav-label">Admissions</span>
                        {((admissionsSubmenuPending?.total || pendingFeesCount) > 0) && (
                          <span className="nav-badge-count" style={{ background: '#f59e0b', color: '#ffffff' }}>
                            {admissionsSubmenuPending?.total || pendingFeesCount}
                          </span>
                        )}
                        <ChevronRight size={14} className={`chevron-arrow ${expandPrincipalAdmissions ? 'rotated' : ''}`} />
                      </>
                    )}
                  </button>

                  {expandPrincipalAdmissions && sidebarOpen && (
                    <div className="cms-subsections-tree">
                      <div className="tree-line-guide" />
                      <div className="subsections-items-wrap">
                        {[
                          { id: 'admissions-periods', title: 'Admission Periods', badge: admissionsSubmenuPending?.periods || 0 },
                          { id: 'admissions-fee-approvals', title: 'Fee Structures', badge: admissionsSubmenuPending?.levels || 0 },
                          { id: 'admissions-test-slots', title: 'Test Sessions', badge: admissionsSubmenuPending?.test_slots || 0 },
                          { id: 'admissions-promo-codes', title: 'Promo Codes', badge: admissionsSubmenuPending?.promo_codes || 0 },
                        ].map((item) => {
                          const isActive = activeTab === item.id;
                          return (
                            <button
                              key={item.id}
                              className={`subsection-link-btn ${isActive ? 'active' : ''}`}
                              onClick={() => handleTabChange(item.id)}
                              title={item.title}
                            >
                              <span className={`subsection-bullet ${isActive ? 'active' : ''}`} />
                              <span className="subsection-text">{item.title}</span>
                              {item.badge > 0 && (
                                <span
                                  className="subsection-count subsection-badge-pending"
                                  title={`${item.badge} pengajuan menunggu persetujuan`}
                                >
                                  {item.badge}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Read-Only Submissions Queue & History (Website Admin) */}
            {showContentManagement && !showApprovalQueue && (
              <button
                className={`nav-item-btn ${activeTab === 'approval-status' || activeTab === 'approval-history' || activeTab === 'submissions' ? 'active' : ''}`}
                onClick={() => handleTabChange('approval-status')}
                title="Content Submissions & Approval Status"
              >
                <FileCheck size={18} className="nav-icon" />
                {sidebarOpen && <span className="nav-label">Submissions</span>}
                {pendingCount > 0 && (
                  <span className="nav-badge-count">{pendingCount}</span>
                )}
              </button>
            )}

            {/* Dedicated Testimonials Management Button */}
            {showContentManagement && (
              <button
                className={`nav-item-btn ${activeTab === 'testimonials-management' ? 'active' : ''}`}
                onClick={() => handleTabChange('testimonials-management')}
                title="Testimonials Review"
              >
                <Quote size={18} className="nav-icon" />
                {sidebarOpen && <span className="nav-label">Testimonials</span>}
              </button>
            )}

            {/* 3. Content Management Hierarchical Accordion (Website Admin) */}
            {showContentManagement && (
              <div className="nav-section-wrapper">
                <button
                  className={`nav-item-btn collapsible ${expandCMS ? 'expanded' : ''}`}
                  onClick={() => setExpandCMS(!expandCMS)}
                  title="Content Management"
                >
                  <Layers size={18} className="nav-icon" />
                  {sidebarOpen && (
                    <>
                      <span className="nav-label">Content Management</span>
                      <ChevronRight size={14} className={`chevron-arrow ${expandCMS ? 'rotated' : ''}`} />
                    </>
                  )}
                </button>

                {expandCMS && sidebarOpen && (
                  <div className="cms-categories-accordion">
                    {WEBSITE_ADMIN_MENUS.map(menu => {
                      const MenuIcon = MENU_ICONS[menu.id] || Layers;
                      const isExpanded = !!expandedMenus[menu.id];
                      const isChildActive = menu.sections?.some(s => s.id === activeTab);

                      return (
                        <div key={menu.id} className="cms-category-block">
                          <button
                            className={`cms-category-header-btn ${isExpanded ? 'expanded' : ''} ${isChildActive ? 'child-is-active' : ''}`}
                            onClick={() => toggleMenuExpanded(menu.id)}
                          >
                            <MenuIcon size={14} className="cat-icon" />
                            <span className="cat-title">{menu.title}</span>
                            <ChevronRight size={13} className={`chevron-cat ${isExpanded ? 'rotated' : ''}`} />
                          </button>

                          {isExpanded && (
                            <div className="cms-subsections-tree">
                              <div className="tree-line-guide" />
                              <div className="subsections-items-wrap">
                                {menu.sections.map(section => {
                                  const isActive = activeTab === section.id;
                                  return (
                                    <button
                                      key={section.id}
                                      className={`subsection-link-btn ${isActive ? 'active' : ''}`}
                                      onClick={() => handleTabChange(section.id)}
                                      title={section.title}
                                    >
                                      <span className={`subsection-bullet ${isActive ? 'active' : ''}`} />
                                      <span className="subsection-text">{section.title}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>

        {/* SIDEBAR FOOTER - USER PROFILE (SATU UNPAM STYLE) */}
        <div className="sidebar-bottom-profile" ref={dropdownRef}>
          <div 
            className={`profile-card-button ${dropdownOpen ? 'active' : ''}`}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            title="User Profile Menu"
          >
            <div className="profile-avatar">{getUserInitials()}</div>
            {sidebarOpen && (
              <>
                <div className="profile-details">
                  <span className="profile-name">{user?.full_name || roleDisplayName}</span>
                  <span className="profile-role">{roleDisplayName}</span>
                </div>
                <ChevronDown size={14} className={`profile-chevron ${dropdownOpen ? 'rotated' : ''}`} />
              </>
            )}
          </div>

          {dropdownOpen && (
            <div className="profile-popover-menu">
              <div className="popover-user-info">
                <span className="popover-full-name">{user?.full_name || roleDisplayName}</span>
                <span className="popover-email">{user?.email || 'admin@permataharapan.sch.id'}</span>
              </div>
              <div className="popover-divider" />
              <button className="popover-btn-logout" onClick={handleLogout}>
                <LogOut size={14} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <div className="portal-main-area">
        {/* TOPBAR (OUTSIDE COLLAPSE BUTTON + DATE + STATUS) */}
        <header className="content-topbar">
          <div className="topbar-left-cluster">
            {/* THE COLLAPSE BUTTON IS OUTSIDE THE SIDEBAR HERE! */}
            <button
              className="outside-sidebar-toggle-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              title={sidebarOpen ? "Collapse navigation sidebar" : "Expand navigation sidebar"}
              aria-label="Toggle navigation"
            >
              <PanelLeft size={18} />
            </button>

            {/* CURRENT DATE DISPLAY */}
            <div className="topbar-date-card">
              <Calendar size={14} className="date-icon" />
              <span>{currentFormattedDate}</span>
            </div>
          </div>

          <div className="topbar-right-cluster">
            {!isOnline ? (
              <div 
                className="system-health-pill offline" 
                title="Koneksi internet terputus. Mohon periksa jaringan Anda."
              >
                <span className="health-dot offline" />
                <WifiOff size={13} className="shield-icon offline" />
                <span>PHS Portal Offline</span>
              </div>
            ) : sessionExpired ? (
              <button 
                type="button"
                className="system-health-pill expired" 
                onClick={() => setShowSessionModal(true)}
                title="Sesi telah berakhir. Klik untuk login ulang"
              >
                <span className="health-dot expired" />
                <AlertTriangle size={13} className="shield-icon expired" />
                <span>Sesi Habis (Login Ulang)</span>
              </button>
            ) : (
              <div 
                className="system-health-pill online" 
                title="Sistem terhubung aktif dan sesi valid"
              >
                <span className="health-dot online" />
                <ShieldCheck size={13} className="shield-icon online" />
                <span>PHS Portal Online</span>
              </div>
            )}
          </div>
        </header>

        {/* SMOOTH NATIVE SCROLLABLE CONTENT BODY */}
        <main className="content-scrollable-body" ref={contentScrollRef}>
          <div className="content-viewport-container">
            {children}
          </div>
        </main>
      </div>

      {/* SESSION EXPIRED MODAL NOTIFICATION */}
      {showSessionModal && (
        <div className="session-modal-backdrop" onClick={(e) => e.stopPropagation()}>
          <div className="session-modal-dialog">
            <div className="session-modal-icon-badge">
              <AlertCircle size={28} />
            </div>
            <h3 className="session-modal-title">Sesi Login Telah Berakhir</h3>
            <p className="session-modal-desc">
              Demi keamanan data administrasi sekolah, sesi login Anda telah berakhir. Silakan login kembali untuk melanjutkan pengelolaan website.
            </p>
            <div className="session-modal-actions">
              <button
                type="button"
                className="session-modal-btn-login"
                onClick={handleLogout}
              >
                Login Ulang Sekarang
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardLayout;
